import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Container,
  FileButton,
  Group,
  Loader,
  Menu,
  Modal,
  Popover,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconArrowLeft,
  IconCalendar,
  IconChevronDown,
  IconCheck,
  IconDeviceFloppy,
  IconPhoto,
  IconPlayerPlay,
  IconQuestionMark,
  IconTrash,
  IconTypography,
} from '@tabler/icons-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useShallow } from 'zustand/react/shallow';
import { RichTextEditor, RichTextEditorProps, Link } from '@mantine/tiptap';
import type { Editor } from '@tiptap/react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { notifications } from '@mantine/notifications';
import DOMPurify from 'dompurify';
import { ChatLayout } from '../../components/layout/ChatLayout';
import { useChatStore } from '../../stores/chatStore';
import { AnnouncementModal } from '../../features/chat/components/AnnouncementModal';
import { ProfileModal } from '../../features/chat/components/ProfileModal';
import { showNotification } from '../../components/Notification';
import {
  useAnnouncementsQuery,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useDeleteAnnouncement,
} from '../../api';
import { trackComponentInteraction } from '../../utils/analytics';
import {
  compareNzCalendarDates,
  isoToNzCalendarDate,
  nzCalendarStartOfDayIso,
  nzCalendarEndOfDayIso,
} from '../../utils/announcementTime';
import styles from './CreateAnnouncementsPage.module.css';

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = [
  'image/*',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/heic',
  'image/heif',
];

type TextStyleValue = 'paragraph' | 'h1' | 'h2' | 'h3';
type HeadingLevel = 1 | 2 | 3;

interface ToolbarControlProps {
  editor: RichTextEditorProps['editor'];
}

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string) ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const textStyles: Array<{ label: string; value: TextStyleValue; description?: string }> = [
  { label: 'Paragraph', value: 'paragraph' },
  { label: 'Heading 1', value: 'h1', description: 'Large heading' },
  { label: 'Heading 2', value: 'h2', description: 'Section heading' },
  { label: 'Heading 3', value: 'h3', description: 'Subsection heading' },
];

const useEditorSelectionObserver = (editor: Editor | null) => {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (!editor) {
      return undefined;
    }
    const rerender = () => forceUpdate((value) => value + 1);

    editor.on('selectionUpdate', rerender);
    editor.on('transaction', rerender);
    editor.on('update', rerender);

    return () => {
      editor.off('selectionUpdate', rerender);
      editor.off('transaction', rerender);
      editor.off('update', rerender);
    };
  }, [editor]);
};

const ToolbarDivider: React.FC = () => (
  <div className={styles.toolbarDivider} role="separator" aria-orientation="vertical" />
);

const TextStyleControl: React.FC<ToolbarControlProps> = ({ editor }) => {
  const [opened, setOpened] = useState(false);
  useEditorSelectionObserver(editor as Editor | null);

  const currentStyle = useMemo(() => {
    if (!editor) {
      return textStyles[0];
    }
    const headingStyle = textStyles.slice(1).find((style) => {
      const level = parseInt(style.value.replace('h', ''), 10) as HeadingLevel;
      return editor.isActive('heading', { level });
    });
    if (headingStyle) {
      return headingStyle;
    }
    return textStyles[0];
  }, [editor, editor?.state]);

  const handleChange = useCallback(
    (value: TextStyleValue) => {
      if (!editor) {
        return;
      }
      const chain = editor.chain().focus();
      if (value === 'paragraph') {
        chain.setParagraph().run();
      } else {
        const level = parseInt(value.replace('h', ''), 10) as HeadingLevel;
        chain.toggleHeading({ level }).run();
      }
      setOpened(false);
    },
    [editor],
  );

  return (
    <Menu
      opened={opened}
      onChange={setOpened}
      withinPortal={false}
      aria-label="Select text style"
    >
      <Menu.Target>
        <RichTextEditor.Control
          aria-label="Text style"
          aria-haspopup="listbox"
          aria-expanded={opened}
          data-active={currentStyle.value !== 'paragraph' ? true : undefined}
          className={styles.textStyleTrigger}
        >
          <span className={styles.textStyleIcon}>
            <IconTypography size={18} stroke={1.5} />
            <IconChevronDown size={14} stroke={1.5} />
          </span>
        </RichTextEditor.Control>
      </Menu.Target>
      <Menu.Dropdown className={styles.textStyleDropdown}>
        {textStyles.map((style) => (
          <Menu.Item
            key={style.value}
            onClick={() => handleChange(style.value)}
            data-selected={currentStyle.value === style.value ? true : undefined}
            rightSection={
              currentStyle.value === style.value ? <IconCheck size={16} stroke={2} /> : undefined
            }
            aria-selected={currentStyle.value === style.value}
            role="option"
          >
            <div className={styles.textStyleOption}>
              <Text className={styles.textStyleOptionLabel}>{style.label}</Text>
              {style.description && (
                <Text className={styles.textStyleOptionDescription}>{style.description}</Text>
              )}
            </div>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};

interface ImageUploadControlProps extends ToolbarControlProps {
  onInsert: (files: File[]) => Promise<void> | void;
}

const ImageUploadControl: React.FC<ImageUploadControlProps> = ({ editor, onInsert }) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (!editor || !files.length) {
        return;
      }

      setIsUploading(true);
      try {
        await onInsert(files);
      } finally {
        setIsUploading(false);
      }
    },
    [editor, onInsert],
  );

  return (
    <FileButton
      onChange={handleFiles}
      accept={ACCEPTED_IMAGE_TYPES.join(',')}
      multiple
      disabled={!editor || isUploading}
    >
      {(props) => (
        <Tooltip label="Insert image" position="bottom" withArrow>
          <RichTextEditor.Control
            {...props}
            aria-label="Insert image"
            disabled={!editor || isUploading}
            data-loading={isUploading ? true : undefined}
            className={styles.imageControl}
          >
            {isUploading ? <Loader size={16} /> : <IconPhoto size={18} stroke={1.5} />}
          </RichTextEditor.Control>
        </Tooltip>
      )}
    </FileButton>
  );
};

export const CreateAnnouncementsPage: React.FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: '/create-announcements' }) as {
    announcementId?: string;
  };
  const announcementId = search?.announcementId;
  const isEditing = Boolean(announcementId);

  const {
    data: announcements = [],
    error: announcementsError,
  } = useAnnouncementsQuery();

  const createAnnouncementMutation = useCreateAnnouncement();
  const updateAnnouncementMutation = useUpdateAnnouncement();
  const deleteAnnouncementMutation = useDeleteAnnouncement();

  const { isProfileModalOpen, toggleProfileModal, closeProfileModal } = useChatStore(
    useShallow((state) => ({
      isProfileModalOpen: state.isProfileModalOpen,
      toggleProfileModal: state.toggleProfileModal,
      closeProfileModal: state.closeProfileModal,
    })),
  );

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [title, setTitle] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isQueryErrorVisible, setIsQueryErrorVisible] = useState(false);

  const announcementsErrorMessage = useMemo(() => {
    if (!announcementsError) {
      return null;
    }
    if (announcementsError instanceof Error) {
      return announcementsError.message;
    }
    return 'Failed to fetch announcements';
  }, [announcementsError]);

  useEffect(() => {
    if (!announcementsErrorMessage) {
      setIsQueryErrorVisible(false);
      return undefined;
    }

    setIsQueryErrorVisible(true);
    const timeout = setTimeout(() => setIsQueryErrorVisible(false), 4000);
    return () => clearTimeout(timeout);
  }, [announcementsErrorMessage]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      TextAlign.configure({
        types: ['paragraph', 'heading'],
      }),
      Placeholder.configure({
        placeholder: 'Provide as much details as you can',
        includeChildren: true,
      }),
      Image.configure({
        allowBase64: true,
        HTMLAttributes: {
          class: styles.inlineImage,
        },
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        'aria-label': 'Announcement body input',
      },
    },
    shouldRerenderOnTransaction: true,
  }) as RichTextEditorProps['editor'];

  const handleInsertImages = useCallback(
    async (files: File[]) => {
      if (!editor) {
        return;
      }

      const validImages: File[] = [];
      const rejectedMessages: string[] = [];

      files.forEach((file) => {
        if (!file.type.startsWith('image/')) {
          rejectedMessages.push(`${file.name} is not a supported image type.`);
          return;
        }
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
          rejectedMessages.push(`${file.name} is larger than 5 MB.`);
          return;
        }
        validImages.push(file);
      });

      if (rejectedMessages.length) {
        trackComponentInteraction(
          'CreateAnnouncementsPage',
          'Insert Image',
          'rich_text_toolbar',
          'image_rejected',
          rejectedMessages.join(' | ')
        );
        notifications.show({
          color: 'red',
          title: 'Image upload failed',
          message: rejectedMessages.join(' '),
        });
      }

      if (!validImages.length) {
        return;
      }

      for (const file of validImages) {
        try {
          const dataUrl = await readFileAsDataUrl(file);
          editor.chain().focus().setImage({ src: dataUrl, alt: file.name }).run();
          trackComponentInteraction(
            'CreateAnnouncementsPage',
            'Insert Image',
            'rich_text_toolbar',
            'image_inserted',
            file.name
          );
        } catch (error) {
          trackComponentInteraction(
            'CreateAnnouncementsPage',
            'Insert Image',
            'rich_text_toolbar',
            'image_insert_error',
            file.name
          );
          notifications.show({
            color: 'red',
            title: 'Insert image failed',
            message: `Could not insert ${file.name}. Please try again.`,
          });
        }
      }
    },
    [editor],
  );

  useEffect(() => {
    if (isEditing && announcementId && editor) {
      const existing = announcements.find((item) => item.announcementID === announcementId);
      if (existing) {
        setTitle(existing.title);
        setStartDate(isoToNzCalendarDate(existing.startDate));
        setEndDate(isoToNzCalendarDate(existing.endDate));
        editor.commands.setContent(existing.content || '<p></p>');
      }
    }
  }, [announcements, isEditing, announcementId, editor]);

  const blockedRanges = useMemo(() => {
    const todayIso = nzCalendarStartOfDayIso(new Date());
    const today = isoToNzCalendarDate(todayIso) ?? new Date();
    today.setHours(0, 0, 0, 0);
    return announcements
      .filter((a) => (isEditing ? a.announcementID !== announcementId : true))
      .map((a) => ({
        start: isoToNzCalendarDate(a.startDate),
        end: isoToNzCalendarDate(a.endDate),
      }))
      .filter((range): range is { start: Date; end: Date } => {
        if (!range.start || !range.end) {
          return false;
        }
        return range.end >= today;
      });
  }, [announcements, isEditing, announcementId]);

  const excludeDate = useCallback(
    (date: Date) => {
      const current = new Date(date);
      current.setHours(0, 0, 0, 0);

      return blockedRanges.some((range) => {
        const start = new Date(range.start);
        start.setHours(0, 0, 0, 0);
        const end = new Date(range.end);
        end.setHours(23, 59, 59, 999);

        return current >= start && current <= end;
      });
    },
    [blockedRanges],
  );

  const resetNotifications = useCallback(() => {
    setLocalError(null);
    setIsQueryErrorVisible(false);
  }, []);

  const handleBack = useCallback(() => {
    trackComponentInteraction(
      'CreateAnnouncementsPage',
      'Back to Announcements',
      'button',
      'click',
      'back_to_list'
    );
    navigate({ to: '/announcements' });
  }, [navigate]);

  const handleProfileToggle = useCallback(() => {
    trackComponentInteraction('Header', 'Profile', 'button', 'click', 'profile_access');
    toggleProfileModal();
  }, [toggleProfileModal]);

  const editorContent = editor?.getHTML() || '';
  const sanitizedBodyText = useMemo(
    () => DOMPurify.sanitize(editorContent, { ALLOWED_TAGS: [] }).trim(),
    [editorContent],
  );
  const hasImageInBody = useMemo(() => /<img\b/i.test(editorContent), [editorContent]);
  const hasBodyContent = sanitizedBodyText.length > 0 || hasImageInBody;
  const hasValidTitle = Boolean(title.trim());
  const hasValidDateRange =
    startDate instanceof Date &&
    endDate instanceof Date &&
    compareNzCalendarDates(startDate, endDate) <= 0;
  const hasRequiredFields = hasBodyContent && hasValidTitle && hasValidDateRange;
  const hasPendingMutation =
    createAnnouncementMutation.isPending ||
    updateAnnouncementMutation.isPending ||
    deleteAnnouncementMutation.isPending;
  const isSaveDisabled = isSubmitting || hasPendingMutation || !hasRequiredFields;

  const validateForm = useCallback(() => {
    if (!startDate || !endDate) {
      return 'Start and end date are required.';
    }
    if (compareNzCalendarDates(startDate, endDate) > 0) {
      return 'End date must be after the start date.';
    }
    if (!title.trim()) {
      return 'Title cannot be empty.';
    }
    if (!hasBodyContent) {
      return 'Body cannot be empty.';
    }
    return null;
  }, [endDate, hasBodyContent, startDate, title]);

  const handleSave = useCallback(async () => {
    resetNotifications();
    const validationMessage = validateForm();
    if (validationMessage) {
      setLocalError(validationMessage);
      trackComponentInteraction(
        'CreateAnnouncementsPage',
        'Save Announcement',
        'button',
        'validation_failed',
        validationMessage
      );
      return;
    }

    const content = editor?.getHTML() || '';
    const payload = {
      title: title.trim(),
      content,
      startDate: nzCalendarStartOfDayIso(startDate!),
      endDate: nzCalendarEndOfDayIso(endDate!),
    };

    setIsSubmitting(true);
    try {
      if (isEditing && announcementId) {
        await updateAnnouncementMutation.mutateAsync({
          announcementID: announcementId,
          updates: payload,
        });
        showNotification('Announcement updated successfully');
        trackComponentInteraction(
          'CreateAnnouncementsPage',
          'Save Announcement',
          'button',
          'save_success',
          `update:${announcementId}`
        );
      } else {
        const newAnnouncementId = crypto.randomUUID();
        await createAnnouncementMutation.mutateAsync({
          announcementID: newAnnouncementId,
          ...payload,
        });
        showNotification('Announcement created successfully');
        trackComponentInteraction(
          'CreateAnnouncementsPage',
          'Save Announcement',
          'button',
          'save_success',
          `create:${newAnnouncementId}`
        );
      }
      navigate({ to: '/announcements' });
    } catch {
      setLocalError('Failed to save announcement. Please try again.');
      trackComponentInteraction(
        'CreateAnnouncementsPage',
        'Save Announcement',
        'button',
        'save_error',
        isEditing && announcementId ? `update:${announcementId}` : 'create'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    announcementId,
    createAnnouncementMutation,
    editor,
    endDate,
    isEditing,
    navigate,
    resetNotifications,
    startDate,
    title,
    updateAnnouncementMutation,
    validateForm,
  ]);

  const handleDelete = useCallback(async () => {
    if (!announcementId) {
      return;
    }
    trackComponentInteraction(
      'CreateAnnouncementsPage',
      'Delete Announcement',
      'button',
      'delete_attempt',
      announcementId
    );
    resetNotifications();
    setIsSubmitting(true);
    try {
      await deleteAnnouncementMutation.mutateAsync(announcementId);
      showNotification('Announcement deleted successfully');
      trackComponentInteraction(
        'CreateAnnouncementsPage',
        'Delete Announcement',
        'button',
        'delete_success',
        announcementId
      );
      navigate({ to: '/announcements' });
    } catch {
      setLocalError('Failed to delete announcement. Please try again.');
      trackComponentInteraction(
        'CreateAnnouncementsPage',
        'Delete Announcement',
        'button',
        'delete_error',
        announcementId
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [announcementId, deleteAnnouncementMutation, navigate, resetNotifications]);

  const openPreviewModal = useCallback(() => {
    trackComponentInteraction(
      'CreateAnnouncementsPage',
      'Preview Announcement',
      'button',
      'open_preview',
      announcementId ?? 'new'
    );
    setIsPreviewOpen(true);
  }, [announcementId]);

  const closePreviewModal = useCallback(() => {
    if (isPreviewOpen) {
      trackComponentInteraction(
        'CreateAnnouncementsPage',
        'Preview Announcement',
        'modal',
        'dismiss_preview',
        announcementId ?? 'new'
      );
    }
    setIsPreviewOpen(false);
  }, [announcementId, isPreviewOpen]);

  const previewContent = useMemo(
    () => ({
      announcementID: announcementId ?? 'preview',
      title: title || 'Preview Title',
      content: editorContent || '<p></p>',
      startDate: startDate ? nzCalendarStartOfDayIso(startDate) : new Date().toISOString(),
      endDate: endDate ? nzCalendarEndOfDayIso(endDate) : new Date().toISOString(),
      active: true,
    }),
    [announcementId, editorContent, endDate, startDate, title],
  );

  const isPreviewDisabled = useMemo(() => {
    return !hasRequiredFields;
  }, [hasRequiredFields]);

  const displayableError = localError || (isQueryErrorVisible ? announcementsErrorMessage : null);

  return (
    <ChatLayout
      showHeaderLeft={false}
      headerAction={
        <div className={styles.headerAction}>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={22} />}
            onClick={handleBack}
            className={styles.backButton}
          >
            Back
          </Button>
          <Popover
            width={240}
            position="bottom-end"
            offset={0}
            shadow="0px 0px 12px 0px rgba(0, 0, 0, 0.24)"
            opened={isProfileModalOpen}
            onChange={(opened) => {
              if (!opened) {
                closeProfileModal();
              }
            }}
            onClose={closeProfileModal}
            styles={{
              dropdown: {
                border: '1px solid #59B836',
                borderRadius: '6px',
                backgroundColor: '#FFFFFF',
                padding: 0,
              },
            }}
          >
            <Popover.Target>
              <ActionIcon
                size={42}
                variant="filled"
                className={styles.profileButton}
                onClick={handleProfileToggle}
                aria-label="Open profile menu"
              >
                <IconQuestionMark size={24} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              <ProfileModal onClose={closeProfileModal} />
            </Popover.Dropdown>
          </Popover>
        </div>
      }
    >
      <Container size="xl" className={styles.container}>
        <div className={styles.page}>
          <Title order={1} className={styles.title}>
            {isEditing ? 'Edit' : 'Create'}
          </Title>

          {displayableError && (
            <Alert
              color="red"
              variant="light"
              withCloseButton
              onClose={resetNotifications}
              className={styles.errorAlert}
            >
              {displayableError}
            </Alert>
          )}

          <Stack gap="lg" className={styles.form}>
            <Group gap="md" className={styles.dateRow}>
              <DatePickerInput
                label="Start Date"
                placeholder="Select"
                value={startDate}
                onChange={setStartDate}
                excludeDate={excludeDate}
                minDate={isEditing ? undefined : new Date()}
                aria-label="Announcement start date"
                leftSection={<IconCalendar size={18} />}
                leftSectionPointerEvents="none"
                valueFormat="DD MMM YYYY"
                classNames={{
                  root: styles.dateInputRoot,
                  label: styles.fieldLabel,
                  input: styles.inputControl,
                  section: styles.inputSection,
                }}
              />
              <DatePickerInput
                label="End Date"
                placeholder="Select"
                value={endDate}
                onChange={setEndDate}
                excludeDate={excludeDate}
                minDate={startDate ?? (isEditing ? undefined : new Date())}
                aria-label="Announcement end date"
                leftSection={<IconCalendar size={18} />}
                leftSectionPointerEvents="none"
                valueFormat="DD MMM YYYY"
                classNames={{
                  root: styles.dateInputRoot,
                  label: styles.fieldLabel,
                  input: styles.inputControl,
                  section: styles.inputSection,
                }}
              />
            </Group>

            <Stack gap="xs" className={styles.fieldGroup}>
              <Text className={styles.fieldLabel}>
                Title <span className={styles.titleHelper}>(80 characters)</span>
              </Text>
              <TextInput
                value={title}
                onChange={(event) => setTitle(event.currentTarget.value)}
                placeholder="Enter announcement title"
                maxLength={80}
                data-autofocus
                classNames={{
                  root: styles.textInputRoot,
                  input: styles.inputControl,
                }}
              />
            </Stack>

            <Stack gap="sm" className={styles.fieldGroup}>
              <Text className={styles.fieldLabel}>Body</Text>
              <RichTextEditor
                editor={editor}
                classNames={{
                  root: styles.richTextRoot,
                  toolbar: styles.richTextToolbar,
                  content: styles.richTextContent,
                  control: styles.richTextControl,
                }}
              >
                <RichTextEditor.Toolbar sticky stickyOffset={0}>
                  <RichTextEditor.ControlsGroup>
                    <ImageUploadControl editor={editor} onInsert={handleInsertImages} />
                    <TextStyleControl editor={editor} />
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Link />
                    <RichTextEditor.Unlink />
                  </RichTextEditor.ControlsGroup>
                  <ToolbarDivider />
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.AlignLeft />
                    <RichTextEditor.AlignCenter />
                    <RichTextEditor.AlignJustify />
                    <RichTextEditor.AlignRight />
                  </RichTextEditor.ControlsGroup>
                  <ToolbarDivider />
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.OrderedList />
                    <RichTextEditor.BulletList />
                  </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>
                <RichTextEditor.Content />
              </RichTextEditor>
            </Stack>

            <Group justify="space-between" align="center" className={styles.actionsRow}>
              <Group gap="md">
                <Button
                  leftSection={<IconDeviceFloppy size={18} />}
                  onClick={handleSave}
                  loading={isSubmitting}
                  disabled={isSaveDisabled}
                  className={styles.saveButton}
                >
                  Save
                </Button>
                <Button
                  leftSection={<IconPlayerPlay size={22} />}
                  variant="subtle"
                  className={styles.previewButton}
                  onClick={openPreviewModal}
                  disabled={!editor || isPreviewDisabled}
                >
                  Preview
                </Button>
              </Group>
              {isEditing && (
                <Button
                  variant="subtle"
                  className={styles.deleteButton}
                  leftSection={<IconTrash size={18} />}
                  onClick={handleDelete}
                  disabled={isSubmitting || deleteAnnouncementMutation.isPending}
                >
                  Delete
                </Button>
              )}
            </Group>
          </Stack>
        </div>
      </Container>

      <Modal
        opened={isPreviewOpen}
        onClose={closePreviewModal}
        size="auto"
        centered
        withCloseButton={false}
        padding="sm"
        styles={{
          content: {
            backgroundColor: 'transparent',
          },
          body: {
            padding: 0,
          },
        }}
      >
        <AnnouncementModal
          announcement={previewContent}
          onClose={closePreviewModal}
        />
      </Modal>
    </ChatLayout>
  );
};
